import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttTable,
    SttChipEventType,
    SttChipEventKind,
} from '../../../sporttia/all';
import DateBuilder from '../../../lib/DateBuilder';
import { formatPriceByLocale } from '../../../lib/utils';

type MshipData = {
    id: number;
};

type Props = {
    mship: MshipData;
};

type ParticipantData = {
    created: string;
    category: {
        name: string;
        event: {
            name: string;
            kind: string;
            type: string;
            ini: string;
            end: string;
        };
    };
    enrollment?: {
        price: number;
    } | null;
};

type ParticipantsResponse = {
    rows?: ParticipantData[];
    total?: number;
};

type PaginationParams = {
    page?: number;
    numRows?: number;
};

export default function MshipEventParticipants({
    mship,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [participants, setEventParticipants] = useState<ParticipantsResponse>(
        {},
    );

    /**
     * Get participants
     */
    const loadEventParticipants = (p: PaginationParams = {}) => {
        cxt?.api('GET', `/mships/${mship.id}/events/participants`, {
            params: {
                page: p.page,
                rows: p.numRows,
            },
            success: (r: any) => {
                setEventParticipants(r);
            },
        });
    };
    // -----| Render |-----
    return (
        <SttTable
            data={participants}
            columns={[
                {
                    field: 'created',
                    title: t('Created'),
                    value: (row: ParticipantData) =>
                        new DateBuilder(
                            row?.created,
                            cxt?.sc?.timezone?.name,
                            cxt?.lang?.key,
                        ).cdlhm(),
                },
                {
                    field: 'eventCategory',
                    title: `${t('Event')} / ${t('Category')}`,
                    align: 'left',
                    value: (r: ParticipantData) => (
                        <>
                            <b>{r.category.event.name}</b>
                            <div>
                                {t('Category')}: <b>{r.category.name}</b>
                            </div>
                        </>
                    ),
                },
                {
                    field: 'kind',
                    title: t('Kind'),
                    value: (r: ParticipantData) => (
                        <SttChipEventKind kind={r.category.event.kind as any} />
                    ),
                },
                {
                    field: 'type',
                    title: t('Inscription'),
                    value: (r: ParticipantData) => (
                        <SttChipEventType type={r.category.event.type as any} />
                    ),
                },
                {
                    field: 'period',
                    title: t('Period'),
                    value: (r: ParticipantData) => ({
                        ini: r.category.event.ini,
                        end: r.category.event.end,
                    }),
                    type: 'period',
                },
                {
                    field: 'price',
                    title: t('Price'),
                    align: 'right',
                    value: (r: ParticipantData) =>
                        r.enrollment?.price !== null
                            ? formatPriceByLocale(
                                  r?.enrollment?.price,
                                  cxt?.sc?.city?.country?.currency,
                              )
                            : null,
                },
            ]}
            onFetch={loadEventParticipants}
        />
    );
}
